/**
 * WorkStreamCreateWizard — guided WorkStream creation (ONBOARDING_FLOW, Story 2).
 *
 * A 3-step MarthaCard-based wizard overlay:
 *   Step 1 — Name      (pre-fillable from TopicPromotionSuggestion.suggested_name)
 *   Step 2 — Trigger   (Schedule / On-Demand / From-Chat chip group)
 *   Step 3 — Blueprint (starter-template gallery from synthesis.md)
 *
 * Submit → onCreate(payload) → caller posts to /workstreams and refreshes the
 * rail. The wizard is decoupled from the network so it is unit-testable: the
 * caller (WorkstreamsPage or ChatPromoteToWorkStream) wires the real create
 * call to `onCreate`.
 *
 * Rendered as a fixed overlay (Radix-free — plain portal-less modal) so it can
 * be hosted from either the WorkStreams surface or the chat promotion
 * affordance without a shared router.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { StepName } from './WorkStreamCreateWizard/StepName';
import { StepTrigger } from './WorkStreamCreateWizard/StepTrigger';
import { StepBlueprint } from './WorkStreamCreateWizard/StepBlueprint';
import { getTemplateById } from './WorkStreamTemplates';
import type {
  CreateWorkStreamPayload,
  WorkStreamTriggerConfig,
  WorkStreamWizardValues,
  TopicPromotionSuggestion,
} from './types';

const TOTAL_STEPS = 3;

export interface WorkStreamCreateWizardProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the assembled payload on submit. Return a Promise to show a
   * pending spinner until the create resolves (the caller posts to /workstreams).
   */
  onCreate: (payload: CreateWorkStreamPayload) => void | Promise<void>;
  /** Optional pre-fill derived from a TopicPromotionSuggestion (Story 3). */
  initialValues?: Partial<WorkStreamWizardValues> & { suggestion?: TopicPromotionSuggestion };
}

function deriveInitialValues(
  initialValues?: WorkStreamCreateWizardProps['initialValues'],
): WorkStreamWizardValues {
  const suggestion = initialValues?.suggestion;
  const triggerType = initialValues?.trigger?.type
    ?? (suggestion ? 'from-chat' : 'on-demand');
  return {
    name: initialValues?.name ?? suggestion?.suggested_name ?? '',
    trigger: {
      type: triggerType,
      cron: initialValues?.trigger?.cron ?? (triggerType === 'schedule' ? '0 9 * * 1' : undefined),
      // Preserve the skip-weekends + holiday-shift policy so a reloaded
      // WorkPlan keeps its schedule settings (round-trip on reload).
      skipWeekends: initialValues?.trigger?.skipWeekends,
      holidayShift: initialValues?.trigger?.holidayShift,
    },
    templateId: initialValues?.templateId ?? null,
    detectedClient: initialValues?.detectedClient ?? suggestion?.detected_client,
    triggerTouched: false,
  };
}

export function WorkStreamCreateWizard({
  open,
  onClose,
  onCreate,
  initialValues,
}: WorkStreamCreateWizardProps) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<WorkStreamWizardValues>(() => deriveInitialValues(initialValues));
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset to step 0 + re-derive pre-fill each time the wizard (re)opens, so a
  // promotion pre-fill from a different chat thread is picked up.
  useEffect(() => {
    if (open) {
      setStep(0);
      setValues(deriveInitialValues(initialValues));
      setNameError(null);
      setSubmitting(false);
    }
  }, [open, initialValues]);

  const close = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const validateName = useCallback((name: string): string | null => {
    if (!name.trim()) return 'Give your WorkStream a name to continue.';
    return null;
  }, []);

  const next = useCallback(() => {
    if (step === 0) {
      const err = validateName(values.name);
      setNameError(err);
      if (err) return;
    }
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  }, [step, values.name, validateName]);

  const back = useCallback(() => {
    setNameError(null);
    setStep(s => Math.max(s - 1, 0));
  }, []);

  const submit = useCallback(async () => {
    // Final guard — name is required even if the user skipped back to step 1.
    const err = validateName(values.name);
    setNameError(err);
    if (err) {
      setStep(0);
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({
        name: values.name.trim(),
        trigger: values.trigger,
        templateId: values.templateId,
        detectedClient: values.detectedClient,
      });
    } finally {
      setSubmitting(false);
    }
  }, [values, validateName, onCreate]);

  // Selecting a template pre-fills name + trigger per the template's
  // suggestedTrigger (Story 4 AC-2) — but only when the user hasn't already
  // made their own choice. Name is seeded only if blank; trigger is seeded
  // only if the user hasn't touched the trigger step yet (Story 2 AC-1: an
  // explicit "Schedule" picked on step 2 must survive selecting a template
  // whose suggestedTrigger differs on step 3).
  const handleTemplateSelect = useCallback((id: string) => {
    setValues(prev => {
      const toggled = prev.templateId === id ? null : id;
      const tpl = getTemplateById(toggled);
      if (!tpl) return { ...prev, templateId: null, triggerTouched: prev.triggerTouched };
      // Only seed the name if the user hasn't typed their own.
      const seededName = prev.name.trim() ? prev.name : tpl.name;
      // Only seed the trigger if the user hasn't picked one on step 2.
      const trigger = prev.triggerTouched
        ? prev.trigger
        : { ...prev.trigger, type: tpl.suggestedTrigger };
      return {
        ...prev,
        templateId: tpl.id,
        name: seededName,
        trigger,
        triggerTouched: prev.triggerTouched,
      };
    });
  }, []);

  const triggerConfig: WorkStreamTriggerConfig = values.trigger;

  const stepContent = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <StepName
            value={values.name}
            onChange={v => {
              setValues(prev => ({ ...prev, name: v }));
              if (nameError) setNameError(null);
            }}
            error={nameError}
          />
        );
      case 1:
        return (
          <StepTrigger
            value={triggerConfig}
            onChange={t => setValues(prev => ({ ...prev, trigger: t, triggerTouched: true }))}
          />
        );
      case 2:
        return (
          <StepBlueprint
            selectedId={values.templateId}
            onSelect={handleTemplateSelect}
          />
        );
      default:
        return null;
    }
  }, [step, values, nameError, triggerConfig, handleTemplateSelect]);

  if (!open) return null;

  const isLast = step === TOTAL_STEPS - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Create a WorkStream"
    >
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-neutral-900/40"
        onClick={close}
        aria-hidden="true"
      />

      {/* MarthaCard container */}
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface-card shadow-warm-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-subtle">
              Step {step + 1} of {TOTAL_STEPS}
            </span>
            <h2 className="text-base font-semibold text-text">
              {step === 0 ? 'Name your WorkStream' : step === 1 ? 'Choose a trigger' : 'Pick a blueprint'}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            aria-label="Close"
            className="p-1.5 rounded-md text-subtle hover:text-text hover:bg-surface-subtle disabled:opacity-40 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-5 pt-3">
          <div className="flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-primary-500' : 'bg-surface-subtle'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5 overflow-y-auto flex-1">{stepContent}</div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-line">
          <button
            type="button"
            onClick={back}
            disabled={step === 0 || submitting}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-muted hover:text-text disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
            Back
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium shadow-warm-sm hover:bg-primary-700 disabled:opacity-60 transition-colors"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {submitting ? 'Creating…' : 'Create WorkStream'}
            </button>
          ) : (
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium shadow-warm-sm hover:bg-primary-700 transition-colors"
            >
              Next
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
